import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RefreshTokenRequestSources, UserRoles } from '../app.enums';
import { AppSettings } from '../app.settings';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LoginModel } from '../components/auth/auth.component';
import { ResetPassword } from '../common/editprofile/editprofile.component';
import { GalleryAddress, GalleryArtTypesDTOs, GalleryItems } from './galleries.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import * as moment from 'moment';
import { States } from './registration.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
  };

  // Auth state ready subject - emits true when APP_INITIALIZER completes
  private authStateReady = new BehaviorSubject<boolean>(false);
  public authStateReady$ = this.authStateReady.asObservable();

  /**
   * Call this method when APP_INITIALIZER completes to notify components
   * that auth state is now stable and ready to be used
   */
  public setAuthStateReady(): void {
    if (!this.authStateReady.value) {
      this.authStateReady.next(true);
    }
  }

  /**
   * Check if auth state initialization is complete
   */
  public isAuthStateReady(): boolean {
    return this.authStateReady.value;
  }

  constructor(@Inject(PLATFORM_ID) readonly platformId: Object,
    private http: HttpClient,
    private jwtHelper: JwtHelperService) {
  }

  //email wala button pa unauthorize ha
  public viewUserInfo(data: ViewUserInfo): Observable<ViewUserInfo> {
    var api: string = '/User';
    var method: string = '/view';
    var url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ViewUserInfo>(url, data, this.httpOptions);
  }



  public deleteAccount(data: DeleteUserInfo): Observable<ResponseObject> {
    var api: string = '/User';
    var method: string = '/delete';
    var url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, data, this.httpOptions);
  }


  public login(data: LoginModel): Observable<ResponseObject> {
    var api: string = '/Auth';
    var method: string = '/signin';
    var url: string = environment.BASE_API_PATH + api + method;
    var postObservable = this.http.post<ResponseObject>(url, data, {
      headers: this.httpOptions.headers,
      withCredentials: true,  //this is important to pass the refresh-token cookie in the request headers
      //observe: 'response'   //if we don't observe the response, it would mean response would only contain body, not headers but we need headers to store the cookie returned as "set-cookie" header in the response
    });

    const subject = new ReplaySubject<ResponseObject>(1);
    subject.subscribe((response: ResponseObject) => {
      if (response.Success && response.ResponseData) {
        var tokenResponse: TokenResponse = response.ResponseData;
        //set the access token in localStorage so we can pass it on to the server plus check for the expiry/login status
        this.setAccessToken(tokenResponse.AccessToken);
        //also set the current user in localStorage so we can retrive it back later
        this.setCurrentUser(tokenResponse.AuthUser);
        sessionStorage.setItem('isUserLoggedIn', 'true');
      }
    }, (error) => { console.error(error); this.handleAuthenticationError(error); });
    postObservable.subscribe(subject);
    return subject;
  }

  public switchAccount(Id: string): Observable<ResponseObject> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.getAccessToken()
      }),
      withCredentials: true,  //this is important to pass the refresh-token cookie in the request headers
      //observe: 'response'   //if we don't observe the response, it would mean response would only contain body, not headers but we need headers to store the cookie returned as "set-cookie" header in the response
    };

    var api: string = '/Auth';
    var method: string = '/switch/' + Id;
    var url: string = environment.BASE_API_PATH + api + method;
    var postObservable = this.http.post<ResponseObject>(url, {}, httpOptions);

    const subject = new ReplaySubject<ResponseObject>(1);
    subject.subscribe((response: ResponseObject) => {
      if (response.Success && response.ResponseData) {
        var tokenResponse: TokenResponse = response.ResponseData;
        //set the access token in localStorage so we can pass it on to the server plus check for the expiry/login status
        this.setAccessToken(tokenResponse.AccessToken);
        //also set the current user in localStorage so we can retrive it back later
        if (tokenResponse.AuthUser) {
          this.setCurrentUser(tokenResponse.AuthUser);
        }
        sessionStorage.setItem('isUserLoggedIn', 'true');
      }
    }, (error) => { console.error(error); this.handleAuthenticationError(error); });
    postObservable.subscribe(subject);
    return subject;
  }

  public exitAccount(): Observable<ResponseObject> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.getAccessToken()
      })
    };

    var api: string = '/Auth';
    var method: string = '/exit-view';
    var url: string = environment.BASE_API_PATH + api + method;
    var postObservable = this.http.post<ResponseObject>(url, {}, httpOptions);

    const subject = new ReplaySubject<ResponseObject>(1);
    subject.subscribe((response: ResponseObject) => {
      if (response.Success && response.ResponseData) {
        var tokenResponse: TokenResponse = response.ResponseData;
        //set the access token in localStorage so we can pass it on to the server plus check for the expiry/login status
        this.setAccessToken(tokenResponse.AccessToken);
        //also set the current user in localStorage so we can retrive it back later
        this.setCurrentUser(tokenResponse.AuthUser);
      }
    }, (error) => { console.error(error); this.handleAuthenticationError(error); });
    postObservable.subscribe(subject);
    return subject;
  }

  //since the refresh token is saved in the http-only cookie so its accessible only on http (server).
  //there's no way we can read or update the http-only cookie in javascript so we need to make a call to server to check if there's
  //a valid refresh token inside the client's browser (which is the http-only cookie)
  public refreshToken(requestSource: RefreshTokenRequestSources): Observable<RefreshTokenResponse> {
    var api: string = '/Auth';
    var method: string = '/refresh-token';
    var url: string = environment.BASE_API_PATH + api + method;
    var postObservable = this.http.post<RefreshTokenResponse>(url, { IsServiceCall: false }, {
      headers: this.httpOptions.headers,
      withCredentials: true,  //this is important to pass the refresh-token cookie in the request headers
    });
    const subject = new ReplaySubject<RefreshTokenResponse>(1);
    subject.subscribe((response: RefreshTokenResponse) => {
      this.onRefreshTokenResponse(requestSource, response);
    }, (error) => { console.error(error); this.handleAuthenticationError(error); });
    postObservable.subscribe(subject);
    return subject;
  }

  private onRefreshTokenResponse(requestSource: RefreshTokenRequestSources, response: RefreshTokenResponse) {
    if (response.Success) {
      if (response.IsValidRefreshToken && response.TokenResponse) {
        console.log('Access token updated on ' + (new Date()) + '. The new token is: ' + response.TokenResponse.AccessToken);
        this.setAccessToken(response.TokenResponse.AccessToken);
        //also set the current user in localStorage so we can retrive it back later
        this.setCurrentUser(response.TokenResponse.AuthUser);
      }
    } else {
      if (requestSource == RefreshTokenRequestSources.AppIntializer) {
        //this request was generated from the APP_INITIALIZER so no need to any error here since there'll always be this request to
        //auto-authenticate the user when he lands on the site (and if he's not logged in meaning there's no refresh token cookie on his browser, there'll be this error)
      } else {
        console.error('Error during refreshing the token');
        console.error(response);
        //error in fetching the new access token from the refresh token
        if (!response.IsRefreshCookieExists) {
          //refresh cookie does not exists
        }
        if (response.IsRefreshCookieExists && !response.IsValidRefreshToken) {
          //refresh token exists in the cookie but is not valid which means that the user has already logged out
        }
      }
    }
  }

  public logout(): Observable<boolean> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.getAccessToken()
      }),
      withCredentials: true,  //this is important to pass the refresh-token cookie in the request headers
      //observe: 'response'   //if we don't observe the response, it would mean response would only contain body, not headers but we need headers to store the cookie returned as "set-cookie" header in the response
    };

    var api: string = '/Auth';
    var method: string = '/signout';
    var url: string = environment.BASE_API_PATH + api + method;

    const subject = new ReplaySubject<boolean>(1);
    this.http.post<boolean>(url, {}, httpOptions).subscribe({
      next: (isDeactivated: boolean) => {
        if (isDeactivated) {
          this.clearLocalAuthState();
        }
        subject.next(isDeactivated);
        subject.complete();
      },
      error: (e) => {
        // The signout request itself failed — most commonly a 401 because the access
        // token has already expired. The user still asked to log out, so clear local
        // auth state and report success rather than leaving them stuck in a dead
        // session. Emit (don't error) so callers can redirect into the signed-out state.
        console.error(e);
        this.clearLocalAuthState();
        subject.next(true);
        subject.complete();
      }
    });
    return subject;
  }

  /** Clears all client-side auth state (access token, current user, session flag). */
  private clearLocalAuthState(): void {
    this.setAccessToken(null);
    this.setCurrentUser(null);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('isUserLoggedIn');
    }
  }

  public postLogoutErrorCleanUp(userId: string): Observable<ResponseObject> {
    var api: string = '/Auth';
    var method: string = '/' + userId + '/postLogoutCleanUp';
    var url: string = environment.BASE_API_PATH + api + method;
    var postObservable = this.http.post<ResponseObject>(url, {}, {
      headers: this.httpOptions.headers,
      withCredentials: true,  //this is important to pass the refresh-token cookie in the request headers
    });

    const subject = new ReplaySubject<ResponseObject>(1);
    subject.subscribe((response: ResponseObject) => {
      console.log(response);
    }, (e) => console.error(e));
    postObservable.subscribe(subject);
    return subject;
  }

  public resetPassword(requestData: ResetPassword): Observable<ResponseObject> {
    var token = this.getAccessToken();
    var httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      })
    }
    var api: string = '/User';
    var method: string = '/reset-password';
    var url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, requestData, { headers: httpOptions.headers });
    // const subject = new ReplaySubject<ResponseObject>(1);
    // subject.subscribe((response: ResponseObject) => {
    //   console.log(response);
    // }, (error) => { console.error(error); this.handleAuthenticationError(error); });
    // postObservable.subscribe(subject);
    // return subject;
  }

  private handleAuthenticationError(err: any) {
    this.setAccessToken(null);
    this.setCurrentUser(null);
  }

  public setAccessToken(accessToken: string | null) {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (!accessToken) {
      localStorage.removeItem(AppSettings.ACCESS_TOKEN);
    } else {
      localStorage.setItem(AppSettings.ACCESS_TOKEN, accessToken);
    }
  }

  public getAccessToken(): string | null {
    if (isPlatformServer(this.platformId)) {
      return null;
    }
    return localStorage.getItem(AppSettings.ACCESS_TOKEN) || '';
  }

  //sets the current user claims in localStorage after user logs in
  public setCurrentUser(user: AuthUser | null) {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (!user) {
      localStorage.removeItem(AppSettings.AUTH_USER);
    } else {
      var json: string = JSON.stringify(user);
      localStorage.setItem(AppSettings.AUTH_USER, btoa(json));
      this.getCurrentUser();
    }
  }

  public IsSuperAdmin() { return (this.getUserRoleId() == UserRoles.SuperAdmin); }
  public isAdministrator() { return (this.getUserRoleId() == UserRoles.Administrator); }
  public isArtist() { return (this.getUserRoleId() == UserRoles.Artist); }
  public isGallery() { return (this.getUserRoleId() == UserRoles.Gallery); }
  public isGalleryStaff() { return (this.getUserRoleId() == UserRoles.GalleryStaff); }

  // Judge and Curator are GalleryStaff with specific GalleryStaffPosition
  public isJudge() {
    return (this.getUserRoleId() == UserRoles.GalleryStaff && this.getGalleryStaffPosition() == 'Judge');
  }
  public isCurator() {
    return (this.getUserRoleId() == UserRoles.GalleryStaff && this.getGalleryStaffPosition() == 'Curator');
  }

  // Get GalleryStaffPosition from JWT token
  public getGalleryStaffPosition(): string | null {
    var decodedToken = this.getDecodedToken();
    return decodedToken?.GalleryStaffPosition || null;
  }

  public getCurrentUser(): AuthUser | null {
    if (isPlatformServer(this.platformId)) {
      return null;
    }

    var token: string | null = this.getAccessToken();
    if (!token || this.jwtHelper.isTokenExpired(token)) {
      //if token is null or exists but expired
      return null;
    }
    var user = null;
    var encodedUserString = localStorage.getItem(AppSettings.AUTH_USER);
    if (encodedUserString) {
      var userJsonString: string = atob(encodedUserString);
      user = JSON.parse(userJsonString);
    }
    return user;
  }

  public isAuthenticated(): boolean {
    var token = this.getAccessToken();
    if (!token) {
      return false;
    }
    return !this.jwtHelper.isTokenExpired(token);
  }

  //if the logged-in user is on a free subscription plan
  public isFreePlanUser(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsFreeAccount == 'True');
  }
  
  public IsProposal(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsProposal == 'True');
  }

  public IsSquarePOS(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsSquarePOS == 'true');
  }

  public IsWoocommerce(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsWoocommerce == 'true');
  }

  public IsShopify(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsShopify == 'true');
  }

  public IsWix(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsWix == 'true');
  }

  public IsSquareSpace():boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsSquareSpace == 'true');
  }

  public IsChatboxMessages(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsChatboxMessages == 'true');
  }

  public IsSocialMediaShares(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsSocialMediaShares == 'true');
  }

  public IsFacebookAccounts(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsFacebookAccounts == 'true');
  }

  public IsInstagramAccounts(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsInstagramAccounts == 'true');
  }

  public proBySource(): string {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.ProBySource);
  }

  //if the logged-in user has an account on woocommerce
  public IsAllowedToSyncProductsOnWooCommerce(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsAllowedToSyncProductsOnWooCommerce == 'True');
  }

  //if the logged-in user is Administrator and they want to viewing artist or gallery
  public isViewingAsAdministrator(): boolean {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.IsViewingAsAdministrator == 'True');
  }

  public getCurrencyCode(): string {
    var decodedToken = this.getDecodedToken();
    return (decodedToken?.CurrencyCode);
  }

  private getDecodedToken(): any {
    var token = this.getAccessToken();
    if (!token || this.jwtHelper.isTokenExpired(token)) {
      return null;
    }
    var decodedToken = this.jwtHelper.decodeToken(token);
    return decodedToken;
  }

  /**
   * Decode and return JWT token (public method for components)
   */
  public decodeToken(token?: string): any {
    if (!token) {
      token = this.getAccessToken() || '';
    }
    if (!token || this.jwtHelper.isTokenExpired(token)) {
      return null;
    }
    return this.jwtHelper.decodeToken(token);
  }

  /**
   * Get GalleryEventId from JWT token claims
   */
  public getGalleryEventIdFromToken(): string | null {
    const decodedToken = this.getDecodedToken();
    return decodedToken?.GalleryEventId || null;
  }

  public getUserId(): string {
    var decodedToken = this.getDecodedToken();
    return decodedToken?.nameid;
  }

  public getTrailExpiryDate(): Date | null {
    var decodeToken = this.getDecodedToken();
    var expiryDate = decodeToken?.InitialTrialExpiryDate;
    if (expiryDate) {
      //we need to parse this date correctly in the format as returned by the API.
      //If we don't do it it reads "dd/MM/yyyy HH:mm" as MM/dd based on the browser settings/location
      const parsedDate = moment(expiryDate, 'DD/MM/YYYY HH:mm').toDate();
      return parsedDate;
    }
    return null;
  }

  public isOnFreetrial(): boolean {
    var decodeToken = this.getDecodedToken();
    var isOnFreeTrial: string = decodeToken?.IsOnFreeTrial;
    if (isOnFreeTrial != null)
      return isOnFreeTrial.toLowerCase() == 'true';
    return false;
  }

  public hasFreeTrialEnded(): boolean | undefined {
    var decodeToken = this.getDecodedToken();
    var hasTrialEnded = decodeToken?.IsFreeTrialEnd;
    if (hasTrialEnded != null)
      return hasTrialEnded.toLowerCase() == 'true';
    return undefined;
  }
  // public isUserSubscribedByStripe(): boolean | undefined {
  //   var decodeToken = this.getDecodedToken();
  //   var isUserSubscribedByStripe = decodeToken?.isUserSubscribedByStripe;
  //   if (isUserSubscribedByStripe != null)
  //     return isUserSubscribedByStripe.toLowerCase() == 'true';
  //   return undefined;
  // }

  public getArtTypeSelection(): number {
    var decodedToken = this.getDecodedToken();
    return decodedToken?.NumberOfArtTypeSelection;
  }

  public getNumberOfPortfolios(): number {
    var decodedToken = this.getDecodedToken();
    return decodedToken?.NumberOfArtistPortfolios;
  }

  public getParrentGalleryId(): string {
    var decodedToken = this.getDecodedToken();
    return decodedToken?.ParrentGalleryId;
  }

public getUserName(): string {
    const decodedToken = this.getDecodedToken();
    if (!decodedToken) {
        console.error("Token could not be decoded");
        return "";
    }
    // Use the "UserName" claim from the token
    return decodedToken.UserName || "";
}

  
  public HideUserOnBoardingCheckList(): boolean {
    var decodedToken = this.getDecodedToken();
    return decodedToken?.HideUserOnBoardingCheckList;
  }
  

  public getUserRoleId(): string {
    var decodedToken = this.getDecodedToken();
    return decodedToken?.role;
  }

  public getAuthConfig(): AuthConfig {
    return {
      IsSuperAdmin: this.IsSuperAdmin(),
      IsAdministrator: this.isAdministrator(),
      IsArtist: this.isArtist(),
      IsGallery: this.isGallery(),
      IsGalleryStaff: this.isGalleryStaff(),
      IsJudge: this.isJudge(),
      IsCurator: this.isCurator()
    };
  }

  public isInAnyRole(allowedRoles: UserRoles[]): boolean {
    if (!allowedRoles) return false;

    var userRoleId = this.getUserRoleId();
    if (userRoleId) {
      return allowedRoles.filter(p => p == userRoleId).length > 0;
    }
    return false;
  }

  // ============================================================================
  // EVENT-ONLY ARTIST METHODS (New Exhibition/Competition Flow)
  // ============================================================================

  /**
   * Register a new artist via event public link (Event-Only Artist)
   * POST /api/Artist/register-via-event
   */
  public registerViaEvent(request: RegisterViaEventRequest): Observable<ResponseObject> {
    var api: string = '/Artist';
    var method: string = '/register-via-event';
    var url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, request, this.httpOptions);
  }

  /**
   * Check if current logged-in artist is an event-only artist
   * GET /api/Artist/is-event-artist
   * Returns: { isEventArtist: true/false }
   */
  public checkIsEventArtist(): Observable<IsEventArtistResponse> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.getAccessToken()
      })
    };
    var api: string = '/Artist';
    var method: string = '/is-event-artist';
    var url: string = environment.BASE_API_PATH + api + method;
    return this.http.get<IsEventArtistResponse>(url, httpOptions);
  }

  /**
   * Upgrade event-only artist to full artist
   * POST /api/Artist/upgrade-to-full-artist
   */
  public upgradeToFullArtist(): Observable<ResponseObject> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.getAccessToken()
      })
    };
    var api: string = '/Artist';
    var method: string = '/upgrade-to-full-artist';
    var url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, {}, httpOptions);
  }

  /**
   * Check if current user is an event-only artist (from JWT token)
   */
  public isEventArtist(): any {
    if (isPlatformServer(this.platformId)) {
      return false;
    }
    const galleryEventId = this.getGalleryEventIdFromToken();
    return !!galleryEventId;
  }

  /**
   * Get the gallery event ID for event-only artist
   */
  public getGalleryEventId(): string | null {
    if (isPlatformServer(this.platformId)) {
      return null;
    }
    return localStorage.getItem('galleryEventId');
  }

  /**
   * Set gallery event ID in localStorage (when user registers via event)
   */
  public setGalleryEventId(galleryEventId: string | null) {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    if (!galleryEventId) {
      localStorage.removeItem('galleryEventId');
    } else {
      localStorage.setItem('galleryEventId', galleryEventId);
    }
  }

  /**
   * Remove gallery event ID (when user upgrades to full artist)
   */
  public removeGalleryEventId() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    localStorage.removeItem('galleryEventId');
  }
}

export class TokenResponse {
  Success!: boolean;
  UserId!: string;
  RoleId!: string;
  AccessToken!: string;
  RefreshToken!: string;
  AuthUser!: AuthUser;
}

export class AuthUser {
  UserId!: string;
  // FullName!: string;
  FirstName!: string;
  LastName!: string
  EmailAddress!: string;
  PhoneNumber!: string;
  LogoUrl!: string;
  DateJoined!: Date;
  BusinessName!: string;
  PersonalImgUrl!: string;
  NumberOfArtTypeSelection!: number
  HideUserOnBoardingCheckList!: boolean
  CompletedUserOnboardingSteps!: number
}
// user-role.enum.ts
export enum proBySourceEnum {
  Artflow = "Artflow",
  FreeTrial = "FreeTrial",
  Stripe = "Stripe",
  AppleInAppPurchase = "AppleInAppPurchase"
}
 

export enum FCMNotificationActionEnum {
  ChatBox = "ChatBox",
  ProductSold = "ProductSold",
  ProductReturn = "ProductReturn",
  ConnectionRequest = "ConnectionRequest", 
  ConnectionAccepted = "ConnectionAccepted",
  ConnectionRejected = "ConnectionRejected",
  ConsignmentDispatch = "ConsignmentDispatch",
  ConsignmentAccepted = "ConsignmentAccepted",
  Proposal = "Proposal",
  AcceptProposal = "AcceptProposal",
  RejectProposal = "RejectProposal",
  ChatBoxLive = "ChatBoxLive"
}

export enum FCMNotificationActionGroupEnum {
  Proposal = "Proposal",
  Product = "Product",
  Connection = "Connection",
  Consignment = "Consignment",
  Chat = "Chat"
}

export enum EmailEventsFilters {
  All = 0,
  SystemDefined = 1,
  UserDefined = 2
}



export class RefreshTokenResponse {
  Success!: boolean;
  ResponseMessage!: string;
  IsRefreshCookieExists!: boolean;
  IsValidRefreshToken!: boolean;
  TokenResponse!: TokenResponse;
}

export class ResetPasswordRequest {
  UserId!: string;
  ResetPasswordToken?: string | null;
  NewPassword!: string;
  ConfirmPassword!: string;
}

export class ResponseObject {
  Success!: boolean;
  ResponseMessage!: string;
  ResponseData: any;
  Errors: string[] = [];
}

export class AuthConfig {
  IsSuperAdmin?: boolean;
  IsAdministrator?: boolean;
  IsArtist?: boolean;
  IsGallery?: boolean;
  IsGalleryStaff?: boolean;
  IsJudge?: boolean;
  IsCurator?: boolean;
}

export class ApplicationUser {
  Id!: string;
  UserName!: string;
  Email!: string;
  Password!: string;
  PhoneNumber!: string;
  ConfirmPassword!: string;
  MobileNumber!: string;
  BusinessLogoUrl!: string;
  Name!: string;
  FirstName!: string;
  LastName!: string;
  AddressLine1!: string;
  AddressLine2!: string;
  Website!: string;
  IsSuperAdmin!: boolean;
  IsAdministrator!: boolean;
  IsArtist!: boolean;
  IsGallery!: boolean;
  LastLoginDate!: Date | null;
  IsBlocked!: boolean;
  AllowLogin!: boolean;
  CreatedBy!: string;
  CreatedDate!: Date | null;
  ModifiedBy!: string;
  ModifiedDate!: Date | null;
  IsDeleted!: boolean;
  DeletedDateTime!: Date | null;
  DeletedBy!: string;
  IsActive!: boolean;
  ImgUrl!: string;
  GalleryImgUrl!: string;
  // BankName!: string;
  // BankAddress!: string;
  // BankBSB!: string;
  // AccountNumber!: string;
  ShortDescription!: string;
  LongDescription!: string;
  SocialMediaDescription!: string;
  BusinessName!: string;
  GalleryType!: string;
  BusinessNumber!: string;
  StreetAddress!: string;
  Suburb!: string;
  PostCode!: string;
  ABN!: string;
  InstagramLink!: string;
  FacebookLink!: string;
  LinkedinLink!: string;
  BusinessFacebookLink!: string;
  BusinessInstagramLink!: string;
  BusinessLinkedinLink!: string;
  IsOpenToInvites!: boolean;
  AboutMe!: string;
  Resume!: string;
  CommissionPercentage!: string;
  PaymentTerms?: string;
  ReturnPolicy?: string;
  TermsConditions?: string;
  GallerySaleType?: string;
  Gallery?: GalleryItems;
  GalleryAddresses!: GalleryAddress[];
  CurrencyCode!: string;
  PrimaryArtTypeId!: string;
  //Helper properties
  State!: any;
  Country!: any;
  City!: any;
  IsSyncToSquarePos!:boolean
  IsSyncToShopify!:boolean
  IsSyncToWooCommerce!:boolean
  IsSyncToWix!:boolean
  ArtistArtTypes: string[] = [];
  FavouritePortfolioLinks: FavouritePortfolioLink[] = [];
  showPortfolio1!: boolean;
  showPortfolio2!: boolean;
  showPortfolio3!: boolean;
 PluginKey?: string | null;
  WixPluginKeyCreatedAt?: Date | null;
  WixPluginKeyIsActive!: boolean;
  PluginKeyUpdatedAt?: Date | null;
  PrimaryProductImageUrl!: string;
  GalleryArtTypesDTOs?: GalleryArtTypesDTOs[]; 
  SalesTaxPercentage!: string
  Terms!: string

}

export class FavouritePortfolioLink {
  Id!: number;
  UserId!: string;
  PortfolioLinkId!: string;
  PortfolioLink!: null;
  CreatedDate!: Date;
  Title!: string;
  Description!: string;
  IsShow!: boolean;
}

export class ViewUserInfo {
  Email!: string;
  Name!: string;
  ImgUrl!: string;
}

export class DeleteUserInfo {
  Email!: string;
  Password!: string;
}

export class FacebookApiresponceObject {
  CreatedDate!: null
  FaceBookEmail!: string;
  FaceBookId!: string;
  FaceBookName!: string;
  FaceBookOuthPages!: FaceBookOuthPages[];
  FacebookAccessTokken!: string;
  FacebookProfilePicture!: string;
  Id!: null;
  UserId!: null;
  responeObject!: ResponseObject;
}

export class FaceBookOuthPages {
  CreatedDate!: string;
  FaceBookOuth!: null;
  FacebookOuthId!: string;
  Id!: string;
  PageAccessTokken!: string;
  PageId!: string;
  PageLink!: string;
  PageName!: string;
  PagePicture!: string;
}

// ============================================================================
// EVENT-ONLY ARTIST INTERFACES
// ============================================================================

/**
 * Request body for registering via event
 */
export class RegisterViaEventRequest {
  galleryEventId!: string;
  firstName!: string;
  lastName!: string;
  email!: string;
  phoneNumber!: string;
  password!: string;
}

/**
 * Response from checking if user is event-only artist
 */
export class IsEventArtistResponse {
  isEventArtist!: boolean;
  galleryEventId?: string;
}
